import type { CategoriesQuery } from 'graphql/types';

type CategoryNode = CategoriesQuery['categories']['data'][0];
type CategoryAttributes = NonNullable<CategoryNode['attributes']>;

type SubCategoryRef = {
  id?: string | null;
  attributes?: {
    title?: string | null;
    translatedName?: string | null;
    order?: number | null;
    subCategories?: { data?: SubCategoryRef[] | null } | null;
  } | null;
};

export type CategorySearchOption = {
  label: string;
  translatedName?: string;
  value: string;
  parentId: string;
  order: number;
};

function mergeCategoryAttributes(
  flat?: CategoryAttributes | null,
  nested?: SubCategoryRef['attributes'],
): CategoryAttributes {
  const base = flat ?? ({} as CategoryAttributes);
  return {
    ...base,
    title: nested?.title ?? base.title,
    translatedName: nested?.translatedName ?? base.translatedName,
    order: nested?.order ?? base.order,
  };
}

function categoryOrder(category: CategoryNode): number {
  return Number(category.attributes?.order ?? 0);
}

/** Register flat categories plus nested subCategories from the homepage tree shape. */
function buildCategoryIndex(categories: CategoryNode[]): Map<string, CategoryNode> {
  const index = new Map<string, CategoryNode>();

  const register = (ref: SubCategoryRef) => {
    if (!ref?.id) return;

    const id = String(ref.id);
    const fromFlat = categories.find((category) => String(category.id) === id);
    const existing = index.get(id);

    index.set(id, {
      id: ref.id,
      attributes: mergeCategoryAttributes(fromFlat?.attributes ?? existing?.attributes, ref.attributes),
    } as CategoryNode);

    const nested = ref.attributes?.subCategories?.data ?? [];
    nested.forEach(register);
  };

  categories.forEach((category) => {
    if (category?.id) {
      index.set(String(category.id), category);
    }
    (category.attributes?.subCategories?.data ?? []).forEach((sub) => register(sub as SubCategoryRef));
  });

  return index;
}

/** Same subcategory resolution as homepage `categoriesList`. */
function resolveSubCategories(parent: CategoryNode, all: CategoryNode[]): CategoryNode[] {
  const ids = parent.attributes?.subCategories?.data?.map((sub) => sub.id) ?? [];
  return ids
    .map((id) => all.find((category) => String(category.id) === String(id)))
    .filter((category): category is CategoryNode => Boolean(category))
    .sort((a, b) => categoryOrder(a) - categoryOrder(b));
}

function resolveTranslatedName(category: CategoryNode): string | undefined {
  return category.attributes?.translatedName?.trim() || undefined;
}

/** Flatten the homepage category tree for TreeSelect (roots + nested subcategories, sorted by `order`). */
export function buildCategorySearchOptions(categories: CategoryNode[] = []): CategorySearchOption[] {
  const categoryIndex = buildCategoryIndex(categories);
  const allCategories = Array.from(categoryIndex.values());
  const options: CategorySearchOption[] = [];

  const addNode = (category: CategoryNode, parentId: string) => {
    options.push({
      label: category.attributes?.title ?? '',
      translatedName: resolveTranslatedName(category),
      value: String(category.id),
      parentId,
      order: categoryOrder(category),
    });

    resolveSubCategories(category, allCategories).forEach((child) => {
      addNode(child, String(category.id));
    });
  };

  allCategories
    .filter((category) => !category.attributes?.parent?.data)
    .sort((a, b) => categoryOrder(a) - categoryOrder(b))
    .forEach((root) => addNode(root, ''));

  return options;
}
